import React, { Fragment, useContext, useEffect, useState } from "react";
import axios from "axios";

import errorHandler from "../../../../../hoc/ErrorHandler";
import authContext from "../../../../../context/auth-context";
import PageTitle from "../../../../UI/PageTitle/PageTitle";

const Notifications = () => {
  const { companyId } = useContext(authContext);
  const parseEnabled = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return ["1", "true", "yes", "on", "enabled", "y", "active"].includes(normalized);
    }
    return false;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: null, text: "" });
  const [form, setForm] = useState({
    provider: "africas_talking",
    enabled: false,
    username: "",
    apiKey: "",
    senderId: "",
    shortCode: "",
    environment: "sandbox",
    testPhone: "",
    testMessage: "Test SMS from eBoard",
  });

  const gatewayUrl = `/companies/${companyId}/sms/gateway`;
  const testUrl = `/companies/${companyId}/sms/gateway/test`;

  useEffect(() => {
    if (!companyId) return;
    let mounted = true;

    (async () => {
      try {
        const response = await axios.get(gatewayUrl);
        const payload = response?.data?.data || response?.data || {};
        const username = payload?.username || payload?.user_name || "";
        const apiKey = payload?.apiKey || payload?.api_key || "";
        const senderId = payload?.senderId || payload?.sender_id || "";
        const shortCode = payload?.shortCode || payload?.short_code || "";
        const hasConfig = Boolean(
          username ||
          apiKey ||
          senderId ||
          shortCode ||
          payload?.id ||
          payload?.gateway_id
        );

        if (mounted) {
          setForm((prev) => ({
            ...prev,
            provider: payload.provider || "africas_talking",
            enabled: hasConfig ? parseEnabled(payload.enabled) : false,
            username,
            apiKey,
            senderId,
            shortCode,
            environment: payload.environment || "sandbox",
          }));
        }
      } catch (_) {
        if (mounted) {
          setForm((prev) => ({
            ...prev,
            provider: "africas_talking",
            enabled: false,
            username: "",
            apiKey: "",
            senderId: "",
            shortCode: "",
            environment: "sandbox",
          }));
          setMessage({
            type: "warning",
            text: "SMS gateway settings not found yet. You can create them below.",
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [companyId, gatewayUrl]);

  const changeHandler = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveHandler = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: null, text: "" });

    try {
      await axios.put(gatewayUrl, {
        provider: "africas_talking",
        enabled: form.enabled,
        username: form.username,
        apiKey: form.apiKey,
        senderId: form.senderId,
        shortCode: form.shortCode,
        environment: form.environment,
      });
      setMessage({ type: "success", text: "SMS gateway settings saved successfully." });
    } catch (error) {
      setMessage({
        type: "danger",
        text: error?.response?.data?.message || "Failed to save SMS gateway settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  const testHandler = async () => {
    if (!form.testPhone) {
      setMessage({ type: "danger", text: "Enter a test phone number first." });
      return;
    }

    setTesting(true);
    setMessage({ type: null, text: "" });
    try {
      await axios.post(testUrl, {
        to: form.testPhone,
        message: form.testMessage || "Test SMS from eBoard",
      });
      setMessage({ type: "success", text: "Test SMS request sent." });
    } catch (error) {
      setMessage({
        type: "danger",
        text: error?.response?.data?.message || "Failed to send test SMS.",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Fragment>
      <PageTitle title="Notification settings" search={false} />
      <div className="page-content">
        <div className="row">
          <div className="col-md-10 col-lg-8">
            <h4 style={{ marginBottom: 8 }}>SMS Gateway (BYOG)</h4>
            <p style={{ marginBottom: 16 }}>
              Configure your company SMS credentials. For now, provider is fixed to Africa&apos;s Talking.
            </p>

            {message.type && (
              <div className={`alert alert-${message.type}`} role="alert">
                {message.text}
              </div>
            )}

            {loading ? (
              <p>Loading SMS gateway settings...</p>
            ) : (
              <form autoComplete="off" onSubmit={saveHandler}>
                <div className="checkbox-custom checkbox-primary">
                  <input
                    id="sms_enabled"
                    type="checkbox"
                    name="enabled"
                    checked={form.enabled}
                    onChange={changeHandler}
                  />
                  <label htmlFor="sms_enabled">Enable company SMS gateway</label>
                </div>

                <div className="form-group form-material floating">
                  <input
                    className="form-control"
                    type="text"
                    value="Africa's Talking"
                    disabled
                  />
                  <label className="floating-label">Provider</label>
                </div>

                <div className="form-group form-material floating">
                  <input
                    className="form-control"
                    type="text"
                    name="username"
                    value={form.username}
                    onChange={changeHandler}
                    required
                  />
                  <label className="floating-label">Africa&apos;s Talking Username</label>
                </div>

                <div className="form-group form-material floating">
                  <input
                    className="form-control"
                    type="password"
                    name="apiKey"
                    value={form.apiKey}
                    onChange={changeHandler}
                    required
                  />
                  <label className="floating-label">Africa&apos;s Talking API Key</label>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group form-material floating">
                      <input
                        className="form-control"
                        type="text"
                        name="senderId"
                        value={form.senderId}
                        onChange={changeHandler}
                      />
                      <label className="floating-label">Sender ID (optional)</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group form-material floating">
                      <input
                        className="form-control"
                        type="text"
                        name="shortCode"
                        value={form.shortCode}
                        onChange={changeHandler}
                      />
                      <label className="floating-label">Short Code (optional)</label>
                    </div>
                  </div>
                </div>

                <div className="form-group form-material floating">
                  <select
                    className="form-control"
                    name="environment"
                    value={form.environment}
                    onChange={changeHandler}
                  >
                    <option value="sandbox">Sandbox</option>
                    <option value="live">Live</option>
                  </select>
                  <label className="floating-label">Environment</label>
                </div>

                <button
                  type="submit"
                  className="btn btn-raised btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save SMS Gateway"}
                </button>
              </form>
            )}

            <hr />

            <h5>Send Test SMS</h5>
            <div className="form-group form-material floating">
              <input
                className="form-control"
                type="text"
                name="testPhone"
                value={form.testPhone}
                onChange={changeHandler}
                placeholder="+2547..."
              />
              <label className="floating-label">Test Phone Number</label>
            </div>
            <div className="form-group form-material floating">
              <input
                className="form-control"
                type="text"
                name="testMessage"
                value={form.testMessage}
                onChange={changeHandler}
              />
              <label className="floating-label">Test Message</label>
            </div>
            <button
              type="button"
              className="btn btn-raised btn-success"
              onClick={testHandler}
              disabled={testing}
            >
              {testing ? "Sending..." : "Send Test SMS"}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default errorHandler(Notifications, axios);
